import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { authState } from "src/store/auth";
import { useUpdateCompanyData } from "src/api/generic";

interface IProps {
  open: boolean;
  handleClose: () => void;
}

const schema = yup.object().shape({
  date: yup.date().required("required"),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const StartBalanceDatePopup = (props: IProps) => {
  const { open, handleClose } = props;
  const { t } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const [loading, setLoading] = useState(false);
  const setNotifications = useSetRecoilState(notificationsState);
  const [auth] = useRecoilState(authState);
  const { user } = auth;
  const max_start_balance_date = user?.company?.max_start_balance_date;

  // APIS
  const { mutate, status, error } = useUpdateCompanyData();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      date: user.company?.close_date,
    },
  });

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { date } = data;

    const isAfterMaxDate = moment(date).isAfter(
      moment(max_start_balance_date, "YYYY-MM-DD"),
    );

    if (max_start_balance_date && isAfterMaxDate) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: `Max balance date is ${max_start_balance_date}`,
        },
      ]);
      return;
    }

    setLoading(true);

    mutate({
      id: user.company.id,
      start_balance_date: moment(date).format("YYYY-MM-DD"),
    });
  };

  //Update success and error handling
  useEffect(() => {
    if (status === "success") {
      window.location.reload();
    } else if (status === "error") {
      setLoading(false);
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  useEffect(() => {
    reset({ date: moment(user.company.start_balance_date).toDate() });
  }, [user]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {generalT("startBalanceDate")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: t(`${errors.date?.message ?? ""}`),
                      size: "small",
                    },
                  }}
                  format={"dd/MM/yyyy"}
                  label={generalT("lockDate")}
                />
              )}
            />
          </Stack>
          <Stack mt={3} spacing={2} direction={"row"}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={loading}
            >
              {generalT("submit")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default StartBalanceDatePopup;
